import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SprayProduct } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import SprayProductForm from './SprayProductForm';

/**
 * Route-level wrapper around SprayProductForm for `/protection/products/:id`.
 *
 * Loads the product by id (via the polymorphic GET /api/v1/consumables/:id) and
 * renders the existing SprayProductForm standalone. Save / cancel navigate back
 * to the products list. The modal-based edit flow on SprayProductsPage is
 * preserved unchanged — both flows coexist by sharing the same form component
 * with different navigation callbacks.
 *
 * Built primarily to give the "Open chemical details" button on the Consumables
 * Catalog detail page (`/consumables/catalog/:id`) a real destination — without
 * this route it produced a blank screen.
 */
const SprayProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [product, setProduct] = useState<SprayProduct | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.products.title'), to: '/protection/products' },
    { label: product?.productName ?? '' },
  ]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    sprayService.getProduct(id)
      .then(p => {
        if (cancelled) return;
        setProduct(p);
        setError('');
      })
      .catch((err: { message?: string }) => {
        if (cancelled) return;
        // We can't depend on `t` in the effect deps without re-fetching on every
        // i18n re-render, so we read it lazily here for the fallback message.
        setError(err?.message ?? t('spray.products.loadError'));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
    // t is intentionally omitted: its reference changes on i18n re-renders and
    // re-running the fetch would put the page back into the Loading state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = useCallback((_saved: SprayProduct) => {
    // SprayProductForm has already issued the PUT via sprayService.updateProduct
    // and invoked onSave with the persisted row. Our job is just to navigate.
    navigate('/protection/products');
  }, [navigate]);

  const handleCancel = useCallback(() => {
    navigate('/protection/products');
  }, [navigate]);

  if (loading) {
    return <div data-testid="spray-product-detail-page">{t('common.loading')}</div>;
  }
  if (error) {
    return (
      <div data-testid="spray-product-detail-page">
        <Alert variant="danger" role="alert">{error}</Alert>
      </div>
    );
  }
  if (!product) {
    return (
      <div data-testid="spray-product-detail-page">
        <Alert variant="warning" role="alert">{t('errors.notFound')}</Alert>
      </div>
    );
  }

  return (
    <div data-testid="spray-product-detail-page">
      <SprayProductForm product={product} onSave={handleSave} onCancel={handleCancel} />
    </div>
  );
};

export default SprayProductDetailPage;
